/**
 * DragDropToggleIsland - Global drag-and-drop toggle for the ramp viewport
 *
 * By default, aircraft are locked (click-to-select only, no drag).
 * This toggle enables free drag for all aircraft on the map.
 * Positioned above ViewPresetsIsland in the bottom-right corner.
 */

import { GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'

interface DragDropToggleIslandProps {
  minimized?: boolean
}

export function DragDropToggleIsland({ minimized = false }: DragDropToggleIslandProps) {
  const isDragDropEnabled = useAircraftEditorStore((s) => s.isDragDropEnabled)
  const setDragDropEnabled = useAircraftEditorStore((s) => s.setDragDropEnabled)

  return (
    <div className={cn(
      'absolute bottom-24 right-4 z-20',
      'transition-all duration-200 ease-out',
      minimized && 'opacity-0 scale-95 pointer-events-none'
    )}>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Island
                className={cn(
                  'px-2.5 py-1.5 cursor-pointer select-none',
                  'hover:bg-background/95',
                  isDragDropEnabled && 'ring-2 ring-amber-400/70 bg-amber-50/80 dark:bg-amber-950/40'
                )}
              >
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <GripVertical className={cn(
                    'h-3.5 w-3.5',
                    isDragDropEnabled ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
                  )} />
                  <span className={cn(
                    'font-medium',
                    isDragDropEnabled ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'
                  )}>
                    Drag & Drop
                  </span>
                  <Switch
                    checked={isDragDropEnabled}
                    onCheckedChange={setDragDropEnabled}
                    className={cn(
                      isDragDropEnabled && 'data-[state=checked]:bg-amber-500 dark:data-[state=checked]:bg-amber-500'
                    )}
                  />
                </label>
              </Island>
            </div>
          </TooltipTrigger>
          <TooltipContent side="left">
            {isDragDropEnabled ? 'Disable free drag — lock aircraft' : 'Enable free drag — unlock all aircraft'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
